import React, { useEffect, useMemo, useState } from "react";
import {
  getBillingById,
  gethotelinfoIdAPI,
  BASE_URL_for_images,
} from "../../helpers/api.ts";
import "./HotelBill.css";
import { Button, Spinner } from "reactstrap";
import html2pdf from "html2pdf.js";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";

const HotelInvoice = ({ checkoutData = null }) => {
  const { id: urlId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [logoHidden, setLogoHidden] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hotelDetails, setHotelDetails] = useState(null);
  const [error, setError] = useState(null);

  const id = checkoutData?.id || urlId;

  const n = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);
  const r2 = (value) => Number(n(value).toFixed(2));
  const money = (value) => r2(value).toFixed(2);

  const getImageAsBase64 = async (imageUrl) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.error("Error converting image to base64:", err);
      return null;
    }
  };

  const fetchHotelDetails = async (hotelId) => {
    try {
      const response = await gethotelinfoIdAPI(hotelId);
      if (response?.success && response?.data) {
        setHotelDetails(response.data);
      }
    } catch (err) {
      console.error("Error fetching hotel details:", err);
    }
  };

  useEffect(() => {
    const fetchInvoiceData = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await getBillingById(id);

        if (response?.success && response?.data) {
          setInvoiceData(response.data);
          setError(null);

          const hotelId = response?.data?.reservation?.hotel?.hotel_id;
          if (hotelId) {
            await fetchHotelDetails(hotelId);
          }
        } else {
          setError(response?.message || "Failed to fetch billing data");
        }
      } catch (err) {
        console.error("Error fetching invoice data:", err);
        setError("An error occurred while fetching billing data.");
      } finally {
        setLoading(false);
      }
    };

    fetchInvoiceData();
  }, [id]);

  const generatePDF = async () => {
    try {
      const element = document.querySelector(".print-section");

      const currentBillingData = invoiceData || {};
      const currentHotelImagePath =
        hotelDetails?.hotel_image ||
        currentBillingData?.reservation?.hotel?.hotel_image ||
        "";

      const currentNormalizedImagePath = currentHotelImagePath.replace(/\\/g, "/");

      if (currentNormalizedImagePath) {
        const imageUrl = `${BASE_URL_for_images}/${currentNormalizedImagePath}`;
        const base64Image = await getImageAsBase64(imageUrl);
        if (base64Image) {
          const imgElement = element.querySelector("img");
          if (imgElement) imgElement.src = base64Image;
        }
      }

      const opt = {
        margin: [8, 8, 8, 8],
        filename: "hotel-invoice.pdf",
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          letterRendering: true,
          allowTaint: true,
          windowWidth: 794,
        },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["css", "legacy"] },
      };

      html2pdf().set(opt).from(element).save();
    } catch (err) {
      console.error("Error generating PDF:", err);
      throw err;
    }
  };

  const handlePrint = async () => {
    try {
      setIsSubmitting(true);
      await generatePDF();
    } catch (err) {
      console.error("Error in print process:", err);
      alert("Failed to complete the operation. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const billingData = invoiceData || {};
  const guest_details = billingData.guest_details || {};
  const latest_details = billingData.latest_details || {};
  const billing_details = billingData.billing_details || {};

  const hotelImagePath =
    hotelDetails?.hotel_image ||
    billingData?.reservation?.hotel?.hotel_image ||
    "";

  const normalizedImagePath = hotelImagePath.replace(/\\/g, "/");

  const hotel_details = {
    hotel_name:
      hotelDetails?.hotel_name || billingData?.reservation?.hotel?.hotel_name || "",
    hotel_number:
      hotelDetails?.hotel_mobile ||
      billingData?.reservation?.hotel?.owner?.mobile ||
      "",
    hotel_email:
      hotelDetails?.hotel_email ||
      billingData?.reservation?.hotel?.owner?.email ||
      "",
    hotel_gst_number:
      hotelDetails?.gst_number || billingData?.reservation?.hotel?.gst_number || "",
    hotel_address:
      hotelDetails?.address || billingData?.reservation?.hotel?.address || "",
    hotel_image: normalizedImagePath,
  };

  const charges = checkoutData?.charges || {
    roomCharges: billingData?.room_charges || 0,
    foodCharges: billingData?.food_charges || 0,
    spaCharges: billingData?.spa_charges || 0,
    addOnCharges: billingData?.add_on_charges || 0,
    extraBedCharges: billingData?.extra_bed_charges || 0,
  };

  const gstRates = checkoutData?.gstRates || {
    roomGst: billingData?.room_gst || 0,
    foodGst: billingData?.food_gst || 0,
    spaGst: billingData?.spa_gst || 0,
  };

  const discount = r2(checkoutData?.discount ?? billingData?.discount ?? 0);
  const advanceAmount = r2(
    billingData?.Amount_Advance ??
      billingData?.reservation?.Amount_Advance ??
      0
  );
  const pendingAmount = r2(billingData?.pending_amount ?? 0);
  const paymentMode =
    checkoutData?.paymentMode ||
    billingData?.payment_mode ||
    billing_details?.payment_mode ||
    "";

  const invoiceTotals = useMemo(() => {
    const roomBase = r2(charges.roomCharges);
    const foodBase = r2(charges.foodCharges);
    const spaBase = r2(charges.spaCharges);
    const addOnBase = r2(charges.addOnCharges);
    const extraBedBase = r2(charges.extraBedCharges);

    const lines = [
      {
        key: "room",
        label: "Room Charges",
        base: roomBase,
        gstRate: r2(gstRates.roomGst) / 100,
        discountable: true,
      },
      {
        key: "food",
        label: "Food & Beverages",
        base: foodBase,
        gstRate: r2(gstRates.foodGst) / 100,
        discountable: false,
      },
      {
        key: "spa",
        label: "Spa Services",
        base: spaBase,
        gstRate: r2(gstRates.spaGst) / 100,
        discountable: false,
      },
      {
        key: "addOn",
        label: "Add-on Charges",
        base: addOnBase,
        gstRate: 0,
        discountable: false,
      },
      {
        key: "extraBed",
        label: "Extra Bed Charges",
        base: extraBedBase,
        gstRate: 0,
        discountable: false,
      },
    ];

    const eligible = lines.filter((x) => x.discountable && x.base > 0);
    const eligibleSubtotal = r2(eligible.reduce((sum, x) => sum + x.base, 0));
    const baseAmount = r2(lines.reduce((sum, x) => sum + x.base, 0));
    const appliedDiscount = r2(Math.min(discount, eligibleSubtotal));

    let usedDiscount = 0;
    const lastEligibleKey =
      eligible.length > 0 ? eligible[eligible.length - 1].key : null;

    const enriched = lines.map((line) => {
      let lineDiscount = 0;

      if (line.discountable && eligibleSubtotal > 0 && appliedDiscount > 0) {
        if (line.key === lastEligibleKey) {
          lineDiscount = r2(appliedDiscount - usedDiscount);
        } else {
          lineDiscount = r2((line.base / eligibleSubtotal) * appliedDiscount);
          usedDiscount = r2(usedDiscount + lineDiscount);
        }
      }

      const net = r2(line.base - lineDiscount);
      const gstAmount = r2(net * line.gstRate);
      const total = r2(net + gstAmount);

      return {
        ...line,
        discount: lineDiscount,
        net,
        gstAmount,
        total,
      };
    });

    const totalGst = r2(enriched.reduce((sum, x) => sum + x.gstAmount, 0));
    const netAfterDiscount = r2(baseAmount - appliedDiscount);
    const billTotal = r2(enriched.reduce((sum, x) => sum + x.total, 0));
    const finalAmountToPay = r2(
      Math.max(billTotal - advanceAmount - pendingAmount, 0)
    );

    const byKey = Object.fromEntries(enriched.map((x) => [x.key, x]));

    return {
      lines: enriched,
      byKey,
      baseAmount,
      discount: appliedDiscount,
      netAfterDiscount,
      totalGst,
      billTotal,
      finalAmountToPay,
    };
  }, [charges, gstRates, discount, advanceAmount, pendingAmount]);

  if (loading) {
    return (
      <div className="m-5 bg-white">
        <div className="text-center p-5">
          <Spinner color="primary" />
          <p className="mt-2">Loading invoice data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="m-5 bg-white">
        <div className="text-center p-5">
          <div className="alert alert-danger" role="alert">
            <hr />
            <p className="mb-0">Please complete the checkout before printing the bill.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content" style={{ margin: "20px" }}>
      <div className="d-print-none mb-3 d-flex gap-2">
        <Button
          className="btn btn-primary"
          onClick={handlePrint}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Processing..." : "Print & Save"}
        </Button>
        <Button
          color="secondary"
          onClick={() => navigate("/reservations")}
          className="w-100 w-md-auto"
        >
          Back
        </Button>
      </div>

      <style>{`
        .invoice-a4-wrapper *,
        .invoice-a4-wrapper *::before,
        .invoice-a4-wrapper *::after {
          box-sizing: border-box;
        }

        @page {
          size: A4 portrait;
          margin: 10mm;
        }

        .invoice-a4-wrapper {
          width: 190mm;
          margin: 0 auto;
          background: #fff;
          box-shadow: 0 0 12px rgba(0,0,0,0.15);
          font-family: Arial, sans-serif;
          font-size: 10px;
          color: #000;
          padding: 0;
        }

        @media print {
          body * { visibility: hidden !important; }
          .invoice-a4-wrapper,
          .invoice-a4-wrapper * { visibility: visible !important; }
          .invoice-a4-wrapper {
            position: fixed !important;
            top: 0;
            left: 0;
            width: 190mm !important;
            box-shadow: none !important;
          }
          .d-print-none { display: none !important; }
        }

        .inv-outer {
          border: 1.5px solid #222;
          padding: 10px 12px;
          width: 100%;
        }

        .inv-header {
          text-align: center;
          margin-bottom: 8px;
        }

        .inv-header img {
          max-width: 80px;
          max-height: 55px;
          object-fit: contain;
          display: block;
          margin: 0 auto 4px auto;
          border-radius: 4px;
        }

        .inv-header h1 {
          font-size: 13px;
          font-weight: 700;
          margin: 0 0 2px 0;
          letter-spacing: 0.5px;
        }

        .inv-header h2 {
          font-size: 11px;
          font-weight: 600;
          margin: 0;
        }

        .inv-hotel-info {
          display: flex;
          justify-content: space-between;
          font-size: 9px;
          margin-bottom: 6px;
          gap: 6px;
          width: 100%;
        }

        .inv-hotel-info p { margin: 1px 0; }
        .inv-hotel-info .right-col { text-align: right; }

        .inv-divider {
          border-top: 1.5px solid #222;
          border-bottom: 1.5px solid #222;
          display: flex;
          margin-bottom: 8px;
          font-size: 9px;
          width: 100%;
        }

        .inv-divider .guest-col {
          width: 50%;
          padding: 6px 8px 6px 0;
          border-right: 1px solid #222;
          word-break: break-word;
        }

        .inv-divider .invoice-col {
          width: 50%;
          padding: 6px 0 6px 8px;
          word-break: break-word;
        }

        .inv-divider p { margin: 2px 0; line-height: 1.4; }

        .inv-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 9.2px;
          margin-bottom: 6px;
          table-layout: fixed;
        }

        .inv-table th {
          background: #f0f0f0;
          border: 1px solid #888;
          padding: 4px 5px;
          font-weight: 700;
          text-align: right;
          word-wrap: break-word;
        }

        .inv-table th:first-child { text-align: left; }

        .inv-table td {
          border: 1px solid #bbb;
          padding: 4px 5px;
          text-align: right;
          line-height: 1.35;
          word-wrap: break-word;
          vertical-align: top;
        }

        .inv-table td:first-child { text-align: left; }
        .inv-table .total-row td { font-weight: 700; background: #f8f8f8; }
        .inv-table .bill-row td { font-weight: 700; background: #eef5ff; }
        .inv-table .final-row td { font-weight: 700; font-size: 10.5px; background: #e8f5e9; }
        .inv-table .advance-row td { background: #fffde7; }
        .inv-table .pending-row td { background: #e3f2fd; }

        .inv-summary-box {
          border: 1px solid #90caf9;
          border-radius: 4px;
          background: #e3f2fd;
          padding: 5px 8px;
          font-size: 9px;
          margin-bottom: 6px;
          width: 100%;
        }

        .inv-summary-box p { margin: 2px 0; }

        .inv-signature {
          display: flex;
          justify-content: space-between;
          margin-top: 8px;
          font-size: 9px;
        }

        .inv-footer-note {
          font-size: 8.5px;
          margin-top: 4px;
          color: #444;
        }

        .inv-table tr,
        .inv-divider,
        .inv-header {
          page-break-inside: avoid;
        }

        .text-danger-lite { color: #c62828; }
        .text-muted-lite { color: #666; }
      `}</style>

      <div className="invoice-a4-wrapper print-section">
        <div className="inv-outer">
          <div className="inv-header">
            {hotel_details?.hotel_image && !logoHidden && (
              <img
                src={`${BASE_URL_for_images}/${hotel_details.hotel_image}`}
                alt={hotel_details?.hotel_name || "Hotel"}
                onError={() => setLogoHidden(true)}
                onLoad={(e) => {
                  // A tall, phone-shaped picture is a screenshot, not a logo.
                  const { naturalWidth: w, naturalHeight: h } = e.target;
                  if (w && h && h / w > 1.3) setLogoHidden(true);
                }}
              />
            )}
            <h1>TAX INVOICE</h1>
            <h2>{hotel_details?.hotel_name || ""}</h2>
          </div>

          <div className="inv-hotel-info">
            <div>
              {hotel_details?.hotel_number && <p>Contact No: {hotel_details.hotel_number}</p>}
              {hotel_details?.hotel_email && <p>Email: {hotel_details.hotel_email}</p>}
            </div>
            <div className="right-col">
              {hotel_details?.hotel_gst_number && <p>GST No: {hotel_details.hotel_gst_number}</p>}
              {hotel_details?.hotel_address && <p>Address: {hotel_details.hotel_address}</p>}
            </div>
          </div>

          <div className="inv-divider">
            <div className="guest-col">
              <p><strong>Guest Name:</strong> {guest_details?.name || ""}</p>
              <p><strong>Contact:</strong> {guest_details?.mobile || ""}</p>
              {guest_details?.address && <p><strong>Address:</strong> {guest_details.address}</p>}
              {guest_details?.vehicle_number && <p><strong>Vehicle No:</strong> {guest_details.vehicle_number}</p>}
              {guest_details?.arrive_from && <p><strong>Arrive From:</strong> {guest_details.arrive_from}</p>}
              {guest_details?.departure_to && <p><strong>Departure To:</strong> {guest_details.departure_to}</p>}
              {paymentMode && <p><strong>Payment Mode:</strong> {paymentMode}</p>}
            </div>

            <div className="invoice-col">
              <p><strong>INVOICE</strong></p>
              <p><strong>Invoice No:</strong> {billingData.invoice_number || billingData.reservation_id || ""}</p>
              <p>
                <strong>Room / Type:</strong>{" "}
                {latest_details?.room?.room_number ||
                  billingData?.reservation?.room?.room_number ||
                  ""}{" "}
                /{" "}
                {latest_details?.room?.room_type ||
                  billingData?.reservation?.room_type ||
                  ""}
              </p>
              <p>
                <strong>Rate/Night:</strong> ₹
                {latest_details?.room?.rate_per_night ||
                  billingData?.reservation?.tariff_charges ||
                  ""}
              </p>
              <p>
                <strong>Nights:</strong>{" "}
                {latest_details?.room?.nights ||
                  billingData?.reservation?.number_of_nights ||
                  ""}
              </p>
              <p>
                <strong>Date:</strong>{" "}
                {new Date().toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}
              </p>
              <p>
                <strong>Arrival:</strong>{" "}
                {moment(
                  latest_details?.room?.check_in ||
                    billingData?.reservation?.check_in_date ||
                    ""
                ).format("DD-MM-YYYY")}{" "}
                {billingData?.reservation?.check_in_time
                  ? `(${billingData.reservation.check_in_time})`
                  : ""}
              </p>
              <p>
                <strong>Departure:</strong>{" "}
                {moment(
                  latest_details?.room?.check_out ||
                    billingData?.reservation?.check_out_date ||
                    ""
                ).format("DD-MM-YYYY")}{" "}
                {billingData?.reservation?.check_out_time
                  ? `(${billingData.reservation.check_out_time})`
                  : ""}
              </p>
              {guest_details?.email && <p><strong>Email:</strong> {guest_details.email}</p>}
            </div>
          </div>

          <table className="inv-table">
            <colgroup>
              <col style={{ width: "26%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "18%" }} />
            </colgroup>
            <thead>
              <tr>
                <th>Service</th>
                <th>Base Amt (₹)</th>
                <th>Discount (₹)</th>
                <th>Net Amt (₹)</th>
                <th>GST Amt (₹)</th>
                <th>Total (₹)</th>
              </tr>
            </thead>
            <tbody>
              {invoiceTotals.lines.map((line) => (
                <tr key={line.key}>
                  <td>{line.key === "food" && line.base > 0 && !line.gstRate ? `${line.label} (GST incl.)` : line.label}</td>
                  <td>{money(line.base)}</td>
                  <td>{line.discount > 0 ? `- ${money(line.discount)}` : "—"}</td>
                  <td>{money(line.net)}</td>
                  <td>{line.gstAmount > 0 ? money(line.gstAmount) : "—"}</td>
                  <td>{money(line.total)}</td>
                </tr>
              ))}

              <tr className="total-row">
                <td colSpan="5"><strong>Base Amount</strong></td>
                <td><strong>₹{money(invoiceTotals.baseAmount)}</strong></td>
              </tr>

              <tr className="total-row">
                <td colSpan="5"><strong>Discount</strong></td>
                <td><strong>- ₹{money(invoiceTotals.discount)}</strong></td>
              </tr>

              <tr className="total-row">
                <td colSpan="5"><strong>Net Amount After Discount</strong></td>
                <td><strong>₹{money(invoiceTotals.netAfterDiscount)}</strong></td>
              </tr>

              <tr className="total-row">
                <td colSpan="5"><strong>Total GST</strong></td>
                <td><strong>₹{money(invoiceTotals.totalGst)}</strong></td>
              </tr>

              <tr className="bill-row">
                <td colSpan="5"><strong>Bill Total (Net + GST)</strong></td>
                <td><strong>₹{money(invoiceTotals.billTotal)}</strong></td>
              </tr>

              {advanceAmount > 0 && (
                <tr className="advance-row">
                  <td colSpan="5">
                    <strong>Advance Amount Paid</strong>
                    <br />
                    <small className="text-muted-lite">(Paid during reservation)</small>
                  </td>
                  <td>
                    <strong style={{ color: "#2e7d32" }}>
                      - ₹{money(advanceAmount)}
                    </strong>
                  </td>
                </tr>
              )}

              {pendingAmount > 0 && (
                <tr className="pending-row">
                  <td colSpan="5">
                    <strong>Amount to be Paid Later</strong>
                    <br />
                    <small className="text-muted-lite">(Will be paid at a later date)</small>
                  </td>
                  <td>
                    <strong style={{ color: "#1565c0" }}>
                      - ₹{money(pendingAmount)}
                    </strong>
                  </td>
                </tr>
              )}

              <tr className="final-row">
                <td colSpan="5"><strong>Final Amount to Pay Now</strong></td>
                <td><strong>₹{money(invoiceTotals.finalAmountToPay)}</strong></td>
              </tr>
            </tbody>
          </table>

          {(advanceAmount > 0 || pendingAmount > 0) && (
            <div className="inv-summary-box">
              <p><strong>Payment Summary</strong></p>
              <p>
                Bill Total: ₹{money(invoiceTotals.billTotal)} &nbsp;|&nbsp;
                Discount: ₹{money(invoiceTotals.discount)} &nbsp;|&nbsp;
                Advance Paid: ₹{money(advanceAmount)}
              </p>
              <p>
                <strong>Pay Now:</strong> ₹{money(invoiceTotals.finalAmountToPay)}
                &nbsp;|&nbsp;
                <strong>Pay Later:</strong> ₹{money(pendingAmount)}
              </p>
            </div>
          )}

          <div className="inv-signature">
            <span>CASHIER __________________</span>
            <span>GUEST'S SIGNATURE __________________</span>
          </div>

          <p className="inv-footer-note">
            This is the final TAX Invoice as per GST Act.
          </p>
        </div>
      </div>
    </div>
  );
};

export default HotelInvoice;